/***
 * Created on 2024/4/15
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useReducer} from "react";
import {useTranslation} from "react-i18next";
import {useTogglePopup} from "../../../../../../hook/toggle_popup";
import {formatDate} from "../../../../../../service/datetime";
import more_svg from "../../../../../../static/img/utils/ic-util-more.svg";
import {usePreCheckPassword} from "../../../../../../hook/pre_check_password";
import VerifyPassword from "../../verify_password";
import {qnapAuthReducer, initState} from "../../../../../../service/qnap_auth_reducer";
import InstallQnapAuthApp from "../install_qnap_auth_app";
import ScanQrCode from "../scan_qr_code";
import QnapAuthAppApproval from "../qnap_auth_app_approval";
import BindQnapAuthAppSuccess from "../bind_qnap_auth_app_success";
import ShowSigninAgainDialog from "../../../../../common/modal_dialog/show_signin_again_dialog";
import SetupAuthApp from "../../setup_dialog/setup_auth_app";
import ManageSetupDialog from "./manage_setup_dialog";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {apiPost} from "../../../../../../service/api";
import QnapAuthSetupSuccess from "./qnap_auth_setup_success";


export const QNAP_AUTH_APP_METHOD_MAP = {
    totp: 'totp',
    qrcode: 'qrcode',
    'login-approval': 'approval',
    'online-verification-code': 'verification'
}

export function QnapAuthPanelRow({method, qnap_auth, label, google_auth}) {
    const {t} = useTranslation();
    const isDefault = qnap_auth?.settings?.default_method === QNAP_AUTH_APP_METHOD_MAP[method] || (!qnap_auth && google_auth);
    const isEnabled = qnap_auth?.settings?.method?.[QNAP_AUTH_APP_METHOD_MAP[method]]?.enabled || google_auth;
    const updated_at = qnap_auth?.settings?.method?.[QNAP_AUTH_APP_METHOD_MAP[method]]?.updated_at || google_auth?.added_on || null;
    const {isShow, toggleAction, elemRef} = useTogglePopup();
    const {checkPassword} = usePreCheckPassword();
    const [state, dispatch] = useReducer(qnapAuthReducer, initState);
    const queryClient = useQueryClient();

    const selectVerifyMethod = (method) => {
        if (method === 'totp') {
            dispatch({
                type: "toggleShowAuthAppSetup",
                value: true,
                method: 'google_auth'
            });
        }
        else {
            dispatch({
                type: "toggleShowInstallApp",
                value: true,
                openAfterInstallApp: "toggleShowQrCodeScan",
                method: QNAP_AUTH_APP_METHOD_MAP[method]
            })
        }
    }

    const onAddNewDevice = async (method) => {
        const norm_method = QNAP_AUTH_APP_METHOD_MAP[method];
        const openAfterPassVerified = ['totp', 'google_auth'].includes(norm_method) ? 'toggleShowAuthAppSetup' : 'toggleShowInstallApp';
        const opts = {
            method: norm_method,
            action: 'update'
        }
        if (openAfterPassVerified === 'toggleShowInstallApp') {
            opts['openAfterInstallApp'] = 'toggleShowQrCodeScan'
        }
        await checkPassword({
            onSuccess: ()=> selectVerifyMethod(method),
            onError: () => dispatch({type: 'toggleVerifyPassword', value: true, openAfterPassVerified, method: norm_method, opts})
        });
    }

    const onSetup = async (method) => {
        if (method === 'totp') {
            await onAddNewDevice(method);
        }
        else {
            const opts = {
                method,
                action: 'setup'
            }
            if (Object.keys(qnap_auth?.devices || {}).length <= 0) {
                await onAddNewDevice(method);
            }
            else {
                await checkPassword({
                    onSuccess: () => dispatch({type: 'toggleManageSetup', value: true}),
                    onError: () => dispatch({
                        type: 'toggleVerifyPassword',
                        value: true,
                        openAfterPassVerified: 'toggleManageSetup',
                        opts
                    })
                });
            }
        }
    }

    const setDefaultMutate = useMutation({
        mutationFn: async (data) => await apiPost('/ajax/qnap_auth/default', data),
        onSuccess: async (data) => {
            await queryClient.invalidateQueries({
                queryKey: ['profile']
            });
        }
    });
    const onSetDefault = async (method) => {
       await setDefaultMutate.mutateAsync({method: QNAP_AUTH_APP_METHOD_MAP[method]});
    }

    const disableMutate = useMutation({
        mutationFn: async (data) => await apiPost('/ajax/qnap_auth/method', data),
        onSuccess: async (data) => {
            if (data?.signin_required) {
                dispatch({type: 'toggleSigninAgain', value: true})
            }
            else {
                await queryClient.invalidateQueries({
                    queryKey: ['profile']
                });
            }
        },
        onError: async (error, variables, context) => {

        }
    });

    const onDisable = async (method) => {
        await disableMutate.mutateAsync({
            method: QNAP_AUTH_APP_METHOD_MAP[method],
            is_enabled: false
        })
    }

    return (
        <div className="two-step--setting__row">
            <div className="two-step--setting__row__col-left">
                <div className={`two-step--setting__method two-step--setting__method--${method}`}>
                    <div className="ic-two-fa-error"></div>
                </div>
            </div>
            <div className="two-step--setting__row__col-center">
                <div className="display-inline-flex align-items-center flex-gap-8 font-medium !pt-4 !pb-4">
                    <div>{label}</div>
                    {isDefault &&
                        <div className="comp-tag">
                            <div className="q-tag q-tag--tiny q-tag--default">
                                <span className="tag__label">
                                    {t('@Default@')}
                                </span>
                            </div>
                        </div>
                    }
                </div>
                <div className="color-dark-gray">
                    {
                        isEnabled &&
                        `${t('@Enabled since:@')} ${updated_at ? formatDate(t, updated_at, true) : ""}`
                    }
                    {
                        !isEnabled && t('@Disabled@')
                    }
                </div>
                <div className="color-red !mt-4 hide">Error message</div>
            </div>
            <div className="two-step--setting__row__col-right">
                {
                    !isEnabled &&
                    <button className="q-btn q-btn--default btn-small btn-rounded-full" onClick={() => onSetup(method)}>
                        <span className="q-btn__label">{t('@Set up@')}</span>
                    </button>
                }
                {
                    isEnabled &&
                    <div className={`util-btn ${isShow ? 'active' : ''}`} util-type="more" color-scheme="normal"
                         ref={elemRef} onClick={toggleAction}>
                        <img src={more_svg} alt=""/>
                        <div className="comp-dropdown menu-comfortable menu-align-right" data-modal-sheet>
                            <ul className="dropdown-menu">
                                {method === 'totp' &&
                                    <>
                                        {!isDefault && <li className="dropdown-menu__item cursor-pointer" onClick={() => onSetDefault(method)}>{t('@Set as default@')}</li>}
                                        <li className="dropdown-menu__item cursor-pointer" onClick={() => onSetup(method)}>{t('@Change Authenticator App@')}</li>
                                        <li className="dropdown-menu__item cursor-pointer !color-red" onClick={() => onDisable(method)}>{t('@Turn off@')}</li>
                                    </>
                                }
                                {method !== 'totp' &&
                                    <>
                                        {!isDefault && <li className="dropdown-menu__item cursor-pointer" onClick={() => onSetDefault(method)}>{t('@Set as default@')}</li>}
                                        <li className="dropdown-menu__item cursor-pointer" onClick={()=>onAddNewDevice(method)}>{t('@Add a new device@')}</li>
                                        <li className="dropdown-menu__item cursor-pointer !color-red" onClick={() => onDisable(method)}>{t('@Turn off@')}</li>
                                    </>
                                }
                            </ul>
                        </div>
                    </div>
                }
            </div>

            {state.showVerifyPassword && <VerifyPassword showModal={state.showVerifyPassword} closeModal={()=>dispatch({type: 'toggleVerifyPassword', value: false})} dispatch={dispatch} state={state}/>}

            {state.showAuthAppSetup && <SetupAuthApp showModal={state.showAuthAppSetup} closeModal={()=>dispatch({type: 'toggleShowAuthAppSetup', value: false})} dispatch={dispatch}/>}

            {state.showInstallApp && <InstallQnapAuthApp showModal={state.showInstallApp} closeModal={()=>dispatch({type: 'toggleShowInstallApp', value: false})} dispatch={dispatch} state={state}/>}
            {state.showQrCodeScan && <ScanQrCode showModal={state.showQrCodeScan} closeModal={()=>dispatch({type: 'toggleShowQrCodeScan', value: false})} dispatch={dispatch} state={state}/>}
            {state.showApproval && <QnapAuthAppApproval showModal={state.showApproval} closeModal={()=>dispatch({type: 'toggleShowApproval', value: false})} dispatch={dispatch} state={state}/>}
            {state.showSuccess && <BindQnapAuthAppSuccess showModal={state.showSuccess} closeModal={()=>dispatch({type: 'toggleShowSuccess', value: false})} dispatch={dispatch} state={state}/>}

            {state.showSigninAgain && <ShowSigninAgainDialog showModal={state.showSigninAgain} closeModal={()=>dispatch({type: 'toggleSigninAgain', value: false})} />}
            {state.showManageSetup && <ManageSetupDialog showModal={state.showManageSetup} closeModal={()=>dispatch({type: 'toggleManageSetup', value: false})} dispatch={dispatch} state={state} method={method} />}

            {state.showQnapAuthSetupSuccess && <QnapAuthSetupSuccess showModal={state.showQnapAuthSetupSuccess} closeModal={() => dispatch({type: 'toggleQnapAuthSetupSuccess', value: false})} dispatch={dispatch} state={state} />}
        </div>
    );

}